<template>
  <v-dialog v-model="dialogModel" max-width="700px" persistent scrollable role="dialog" :aria-labelledby="formTitle">
    <v-card class="group-form-card">
      <v-toolbar class="px-4" flat color="grey lighten-4">
        <v-toolbar-title id="formTitle">
          <v-layout align-center>
            <v-icon left class="grey--text text--darken-2">{{ editMode ? "mdi-pencil" : "mdi-plus" }}</v-icon>
            <span class="title grey--text text--darken-3">{{
              editMode ? $i18n.t("Edit Group") : $i18n.t("Create Group")
            }}</span>
          </v-layout>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog" :aria-label="$i18n.t('Close dialog')">
          <v-icon class="grey--text text--darken-2">mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-4">
        <v-form ref="form" v-model="valid" @submit.prevent="saveGroup">
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12 sm6>
                <v-text-field
                  v-model="groupModel.shortName"
                  :label="$i18n.t('Short Name')"
                  :rules="shortNameRules"
                  required
                  :disabled="editMode"
                  :aria-required="true"
                  :aria-invalid="!valid"
                  :error-messages="shortNameError"
                  @input="shortNameError = ''"
                  prepend-icon="mdi-tag-outline"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field
                  v-model="groupModel.fullName"
                  :label="$i18n.t('Full Name')"
                  prepend-icon="mdi-tag-text-outline"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <v-layout wrap>
              <v-flex xs12 sm6>
                <v-select
                  v-model="groupModel.type"
                  :items="typeOptions"
                  :label="$i18n.t('Type')"
                  prepend-icon="mdi-shape-outline"
                  item-text="text"
                  item-value="value"
                >
                  <template slot="item" slot-scope="{ item }">
                    <v-list-tile-content>
                      <v-list-tile-title>{{ item.text }}</v-list-tile-title>
                    </v-list-tile-content>
                  </template>
                </v-select>
              </v-flex>
              <v-flex xs12 sm6>
                <v-autocomplete
                  v-model="groupModel.parent"
                  :items="parentOptions"
                  :label="$i18n.t('Parent Group')"
                  item-text="text"
                  item-value="value"
                  prepend-icon="mdi-source-branch"
                  clearable
                  :disabled="!parentOptions.length"
                  :loading="loadingParents"
                  :no-data-text="$i18n.t('No parent groups available')"
                >
                </v-autocomplete>
              </v-flex>
            </v-layout>

            <v-layout wrap>
              <v-flex xs12 sm6>
                <v-autocomplete
                  v-model="groupModel.client"
                  :items="clientOptions"
                  :label="$i18n.t('Client')"
                  item-text="text"
                  item-value="value"
                  prepend-icon="mdi-domain"
                  clearable
                  :disabled="!clientOptions.length"
                  :loading="loadingClients"
                  :no-data-text="$i18n.t('No clients available')"
                  @change="handleClientChange"
                >
                </v-autocomplete>
              </v-flex>
              <v-flex xs12 sm6>
                <v-autocomplete
                  v-model="groupModel.contract"
                  :items="filteredContractOptions"
                  :label="$i18n.t('Contract')"
                  item-text="text"
                  item-value="value"
                  prepend-icon="mdi-file-document-outline"
                  clearable
                  :disabled="!filteredContractOptions.length"
                  :loading="loadingContracts"
                  :no-data-text="$i18n.t('No contracts available')"
                  @change="handleContractChange"
                >
                </v-autocomplete>
              </v-flex>
            </v-layout>

            <v-layout wrap>
              <v-flex xs12>
                <v-text-field
                  v-model="groupModel.address"
                  :label="$i18n.t('Address')"
                  prepend-icon="mdi-map-marker"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <v-layout wrap>
              <v-flex xs12>
                <v-autocomplete
                  v-model="groupModel.manager"
                  :items="userOptions"
                  :label="$i18n.t('Manager')"
                  item-text="text"
                  item-value="value"
                  prepend-icon="mdi-account-tie"
                  clearable
                  :disabled="!userOptions.length"
                  :loading="loadingUsers"
                  :no-data-text="$i18n.t('No users available')"
                >
                </v-autocomplete>
              </v-flex>
            </v-layout>

            <v-layout wrap>
              <v-flex xs12>
                <v-textarea
                  v-model="groupModel.description"
                  :label="$i18n.t('Description')"
                  prepend-icon="mdi-text-box-outline"
                  auto-grow
                  rows="3"
                ></v-textarea>
              </v-flex>
            </v-layout>
          </v-container>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-3 grey lighten-4">
        <v-spacer></v-spacer>
        <v-btn flat @click="closeDialog" :aria-label="$i18n.t('Cancel')">
          {{ $i18n.t("Cancel") }}
        </v-btn>
        <v-btn
          color="grey darken-3"
          :disabled="!valid || isLoading"
          @click="saveGroup"
          :loading="isLoading"
          :aria-label="editMode ? $i18n.t('Update group') : $i18n.t('Create group')"
        >
          <v-icon left small>{{ editMode ? "mdi-content-save" : "mdi-plus" }}</v-icon>
          {{ editMode ? $i18n.t("Update") : $i18n.t("Create") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import { GROUP_TYPE } from "../../constants";

export default {
  name: "GroupForm",
  props: {
    dialog: {
      type: Boolean,
      required: true
    },
    editMode: {
      type: Boolean,
      default: false
    },
    group: {
      type: Object,
      default: () => ({
        shortName: "",
        fullName: "",
        type: "",
        address: "",
        description: "",
        parent: null,
        manager: null,
        client: null,
        contract: null,
        users: []
      })
    }
  },
  data() {
    return {
      valid: false,
      groupModel: { ...this.group },
      loadingParents: false,
      loadingUsers: false,
      loadingClients: false,
      loadingContracts: false,
      shortNameError: "",
      typeOptions: GROUP_TYPE.map(group => ({ text: this.$i18n.t(group.toUpperCase()), value: group })),
      shortNameRules: [
        v => !!v || this.$i18n.t("Short name is required"),
        v => (v && v.length >= 2) || this.$i18n.t("Short name must be at least 2 characters"),
        v => (v && v.length <= 50) || this.$i18n.t("Short name must be less than 50 characters"),
        v =>
          (v && /^[a-zA-Z0-9-_]+$/.test(v)) ||
          this.$i18n.t("Short name can only contain letters, numbers, hyphens and underscores")
      ]
    };
  },
  computed: {
    ...mapGetters({
      groups: "group/getGroups",
      isLoading: "group/isLoading",
      users: "users/getUsers",
      clients: "client/getClients",
      contracts: "contract/getContractsMeta"
    }),
    dialogModel: {
      get() {
        return this.dialog;
      },
      set(value) {
        if (!value) {
          this.$emit("close");
        }
      }
    },
    parentOptions() {
      return this.groups
        .filter(g => !this.editMode || g._id !== this.group._id)
        .map(g => ({
          text: g.fullName || g.shortName,
          value: g._id
        }));
    },
    userOptions() {
      return this.users.map(user => ({
        text: user.name,
        value: user._id
      }));
    },
    clientOptions() {
      return this.clients.map(client => ({
        text: client.name,
        value: client._id
      }));
    },
    contractOptions() {
      return this.contracts.map(contract => ({
        text: contract.name,
        value: contract._id,
        clientId: contract.clientId
      }));
    },
    filteredContractOptions() {
      if (!this.groupModel.client) {
        return this.contractOptions;
      }
      return this.contractOptions.filter(contract => contract.clientId == this.groupModel.client);
    },
    formTitle() {
      return this.editMode ? this.$i18n.t("Edit Group") : this.$i18n.t("Create Group");
    }
  },
  watch: {
    group: {
      handler(newVal) {
        this.groupModel = { ...newVal };
      },
      deep: true
    }
  },
  created() {
    this.fetchParentGroups();
    this.fetchUsers();
    this.fetchClients();
    this.fetchContracts();
  },
  methods: {
    async fetchParentGroups() {
      this.loadingParents = true;
      try {
        await this.$store.dispatch("group/fetchGroups");
      } catch (error) {
        console.error("Error fetching parent groups:", error);
      } finally {
        this.loadingParents = false;
      }
    },
    async fetchUsers() {
      this.loadingUsers = true;
      try {
        await this.$store.dispatch("users/fetchUsers");
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        this.loadingUsers = false;
      }
    },
    async fetchClients() {
      this.loadingClients = true;
      try {
        await this.$store.dispatch("client/fetchClients");
      } catch (error) {
        console.error("Error fetching clients:", error);
      } finally {
        this.loadingClients = false;
      }
    },
    async fetchContracts() {
      this.loadingContracts = true;
      try {
        await this.$store.dispatch("contract/fetchContractsMeta");
      } catch (error) {
        console.error("Error fetching contracts:", error);
      } finally {
        this.loadingContracts = false;
      }
    },
    handleContractChange() {
      if (this.groupModel.contract) {
        const selectedContract = this.contractOptions.find(contract => contract.value === this.groupModel.contract);
        console.log("Selected contract:", selectedContract);
        if (selectedContract) {
          this.groupModel.client = selectedContract.clientId;
        } else {
          this.groupModel.client = null;
        }
      }
    },
    handleClientChange() {
      if (this.groupModel.client && this.groupModel.contract) {
        const contractBelongsToClient = this.contractOptions.some(
          contract => contract.value === this.groupModel.contract && contract.clientId === this.groupModel.client
        );

        if (!contractBelongsToClient) {
          this.groupModel.contract = null;
        }
      }
    },
    async saveGroup() {
      if (!this.$refs.form.validate()) return;

      try {
        if (this.editMode) {
          await this.$store.dispatch("group/updateGroup", {
            groupId: this.group._id,
            groupData: this.groupModel
          });
        } else {
          try {
            await this.$store.dispatch("group/createGroup", this.groupModel);
          } catch (error) {
            if (error.response && error.response.status === 409) {
              this.shortNameError = this.$i18n.t("This short name is already in use");
              return;
            }
            throw error;
          }
        }
        this.$emit("saved");
      } catch (error) {
        console.error("Error saving group:", error);
        this.$store.dispatch("ui/displaySnackbar", {
          message: this.$i18n.t("Error saving group"),
          color: "error",
          role: "alert"
        });
      }
    },
    closeDialog() {
      this.$emit("close");
    }
  }
};
</script>

<style scoped>
.group-form-card {
  border-radius: 2px;
  overflow: hidden;
}

.v-btn:focus {
  outline: 1px solid rgba(0, 0, 0, 0.2);
  outline-offset: 1px;
}
</style>
